import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Pencil, Users, Store as StoreIcon, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Label } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const BUILTINS = [
  ["friendliness", "Friendliness"],
  ["speed", "Speed"],
  ["accuracy", "Accuracy"],
  ["knowledge", "Knowledge"],
  ["cleanliness", "Cleanliness"],
];
const BUILTIN_LABELS = Object.fromEntries(BUILTINS);
const BUILTIN_KEYS = BUILTINS.map(([k]) => k);
const CUSTOM_PREFIX = "cdim_";
const MAX_CUSTOM = 7;

const normalise = (list) =>
  (list || [])
    .map((it) => (typeof it === "string"
      ? { key: it, label: BUILTIN_LABELS[it] || it, scope: "waiter" }
      : { scope: "waiter", ...it }))
    .filter((d) => d && d.key);

function AddMenu({ availableBuiltins, canAddCustom, onAddBuiltin, onAddCustom, scope, testId }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className="relative inline-block" data-testid={testId}>
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`${testId}-trigger`}
        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${
          scope === "store"
            ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            : "border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
        }`}>
        <Plus className="h-3.5 w-3.5" /> Add dimension
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={close} />
          <div className="absolute left-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
            data-testid={`${testId}-menu`}>
            {availableBuiltins.length === 0 && !canAddCustom && (
              <div className="px-3 py-2 text-xs text-slate-400">Nothing left to add.</div>
            )}
            {availableBuiltins.map(([k, l]) => (
              <button key={k} type="button" onClick={() => { onAddBuiltin(k); close(); }}
                data-testid={`${testId}-opt-${k}`}
                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50">{l}</button>
            ))}
            {canAddCustom && (
              <button type="button" onClick={() => { onAddCustom(); close(); }}
                data-testid={`${testId}-opt-custom`}
                className="block w-full border-t border-slate-100 px-3 py-2 text-left text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
                + Custom dimension…
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function DimChip({ d, onRemove, onEdit, onToggleRecovery, scope }) {
  const [editing, setEditing] = useState(!d.label && !BUILTIN_KEYS.includes(d.key));
  const [draft, setDraft] = useState(d.label || "");
  useEffect(() => { setDraft(d.label || ""); }, [d.label]);
  const isCustom = !BUILTIN_KEYS.includes(d.key);
  const tone = scope === "store"
    ? "border-amber-300 bg-amber-50 text-amber-900"
    : "border-indigo-300 bg-indigo-50 text-indigo-900";

  const commit = () => {
    const v = draft.trim().slice(0, 40);
    if (!v) return;
    onEdit(v);
    setEditing(false);
  };

  return (
    <div className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-semibold ${tone}`}
      data-testid={`dim-chip-${d.key}`}>
      {editing ? (
        <>
          <input autoFocus value={draft} maxLength={40}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
            data-testid={`dim-edit-${d.key}`}
            placeholder="E.g. Wait time"
            className="w-32 border-0 bg-transparent p-0 text-sm font-semibold focus:outline-none focus:ring-0" />
          <button type="button" onClick={commit} data-testid={`dim-commit-${d.key}`}
            className="rounded-full p-0.5 text-emerald-700 hover:bg-emerald-100" aria-label="Confirm">
            <Check className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <>
          <span>{d.label}</span>
          {isCustom && (
            <button type="button" onClick={() => setEditing(true)} data-testid={`dim-edit-trigger-${d.key}`}
              className="rounded-full p-0.5 text-slate-500 hover:bg-slate-100" aria-label="Rename">
              <Pencil className="h-3 w-3" />
            </button>
          )}
        </>
      )}
      <button type="button" onClick={() => onToggleRecovery(!d.recovery_trigger)}
        data-testid={`dim-recovery-${d.key}`}
        title={d.recovery_trigger
          ? "Rating < 3 on this criterion opens a service-recovery case. Click to disable."
          : "Toggle: open a service-recovery case when this criterion rates below 3."}
        className={`rounded-full p-0.5 ${
          d.recovery_trigger
            ? "bg-red-100 text-red-600 ring-1 ring-red-300"
            : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        }`}
        aria-label={d.recovery_trigger ? "Recovery trigger on" : "Recovery trigger off"}
        aria-pressed={!!d.recovery_trigger}>
        <AlertTriangle className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onRemove} data-testid={`dim-remove-${d.key}`}
        className="rounded-full p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-700" aria-label="Remove">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ScopeColumn({ scope, title, subtitle, dims, availableBuiltins, canAddCustom, onAddBuiltin, onAddCustom, onEdit, onRemove, onToggleRecovery }) {
  const Icon = scope === "store" ? StoreIcon : Users;
  const headerTone = scope === "store"
    ? "bg-amber-50 text-amber-900 border-amber-200"
    : "bg-indigo-50 text-indigo-900 border-indigo-200";
  return (
    <div className={`flex-1 rounded-xl border bg-white ${scope === "store" ? "border-amber-200" : "border-indigo-200"}`}
      data-testid={`scope-col-${scope}`}>
      <div className={`flex items-center gap-2 rounded-t-xl border-b px-4 py-3 ${headerTone}`}>
        <Icon className="h-4 w-4" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider">{title}</div>
          <div className="text-xs opacity-70">{subtitle}</div>
        </div>
      </div>
      <div className="min-h-[72px] p-3">
        <div className="flex flex-wrap items-center gap-2">
          {dims.length === 0 && (
            <span className="text-xs text-slate-400">Nothing yet. Tap "Add dimension".</span>
          )}
          {dims.map((d) => (
            <DimChip key={d.key} d={d} scope={scope}
              onEdit={(label) => onEdit(d.key, label)}
              onToggleRecovery={(v) => onToggleRecovery(d.key, v)}
              onRemove={() => onRemove(d.key)} />
          ))}
        </div>
        <div className="mt-3">
          <AddMenu scope={scope} availableBuiltins={availableBuiltins} canAddCustom={canAddCustom}
            onAddBuiltin={onAddBuiltin} onAddCustom={onAddCustom} testId={`add-${scope}`} />
        </div>
      </div>
    </div>
  );
}

export function RatingFormSettings() {
  const [s, setS] = useState(null);
  const [dims, setDims] = useState([]);

  useEffect(() => {
    api.get("/organization")
      .then((r) => { setS(r.data.settings || {}); setDims(normalise(r.data.settings?.touchpoint_dimensions)); })
      .catch(() => { setS({}); setDims([]); });
  }, []);

  const waiterDims = useMemo(() => dims.filter((d) => (d.scope || "waiter") === "waiter"), [dims]);
  const storeDims = useMemo(() => dims.filter((d) => d.scope === "store"), [dims]);
  const customCount = useMemo(() => dims.filter((d) => !BUILTIN_KEYS.includes(d.key)).length, [dims]);
  const usedBuiltinKeys = useMemo(() => new Set(dims.filter((d) => BUILTIN_KEYS.includes(d.key)).map((d) => d.key)), [dims]);
  const availableBuiltins = useMemo(() => BUILTINS.filter(([k]) => !usedBuiltinKeys.has(k)), [usedBuiltinKeys]);

  const addBuiltinToScope = (scope) => (key) => {
    setDims((cur) => [...cur, { key, label: BUILTIN_LABELS[key], scope }]);
  };
  const addCustomToScope = (scope) => () => {
    if (customCount >= MAX_CUSTOM) {
      toast.error(`You can add up to ${MAX_CUSTOM} custom dimensions`);
      return;
    }
    const key = CUSTOM_PREFIX + Math.random().toString(36).slice(2, 10);
    setDims((cur) => [...cur, { key, label: "", scope }]);
  };
  const editLabel = (key, label) =>
    setDims((cur) => cur.map((d) => (d.key === key ? { ...d, label: label.slice(0, 40) } : d)));
  const toggleRecovery = (key, v) =>
    setDims((cur) => cur.map((d) => (d.key === key ? { ...d, recovery_trigger: !!v } : d)));
  const removeDim = (key) => setDims((cur) => cur.filter((d) => d.key !== key));

  const save = async () => {
    const clean = dims.filter((d) => BUILTIN_KEYS.includes(d.key) || (d.label || "").trim().length);
    try {
      const { data } = await api.patch("/organization/settings", {
        touchpoint_dimensions: clean,
        recovery_sla_hours: Number(s.recovery_sla_hours || 24),
      });
      setS(data);
      setDims(normalise(data.touchpoint_dimensions));
      toast.success("Touchpoints & recovery settings saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="form-recovery-settings">
      <Label>1 · Touchpoint scores (1–5 taps, shown after the star rating)</Label>
      <p className="mt-2 text-sm text-slate-500">
        Add each dimension to the column it belongs in. <span className="font-semibold text-indigo-700">Waiter</span> dimensions count toward the server's SSI;
        {" "}<span className="font-semibold text-amber-700">Store</span> dimensions only feed the Store KPI ranking.
      </p>

      <div className="mt-4 flex flex-col gap-3 lg:flex-row" data-testid="dim-columns">
        <ScopeColumn scope="waiter" title="Rate your server" subtitle="Counts toward each waiter's SSI"
          dims={waiterDims} availableBuiltins={availableBuiltins} canAddCustom={customCount < MAX_CUSTOM}
          onAddBuiltin={addBuiltinToScope("waiter")} onAddCustom={addCustomToScope("waiter")}
          onEdit={editLabel} onToggleRecovery={toggleRecovery} onRemove={removeDim} />
        <ScopeColumn scope="store" title="Rate the store" subtitle="Feeds Store KPI ranking only"
          dims={storeDims} availableBuiltins={availableBuiltins} canAddCustom={customCount < MAX_CUSTOM}
          onAddBuiltin={addBuiltinToScope("store")} onAddCustom={addCustomToScope("store")}
          onEdit={editLabel} onToggleRecovery={toggleRecovery} onRemove={removeDim} />
      </div>
      <p className="mt-2 text-xs text-slate-400">
        Up to {MAX_CUSTOM} custom dimensions across both columns. {customCount}/{MAX_CUSTOM} used.
        <span className="ml-2 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-red-500" /> on a chip auto-opens a service-recovery case when that criterion rates below 3 — even if the overall rating is 4 or 5.</span>
      </p>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-5">
        <div>
          <Label>Service recovery SLA</Label>
          <p className="mt-1 text-xs text-slate-500">Hours to acknowledge a low rating</p>
          <Input type="number" min={1} max={168} className="mt-1 w-32" value={s.recovery_sla_hours ?? 24}
            onChange={(e) => setS({ ...s, recovery_sla_hours: e.target.value })} data-testid="recovery-sla-hours" />
        </div>
        <Button onClick={save} data-testid="save-form-recovery" className="bg-[#4338CA] hover:bg-[#3730A3]">Save</Button>
      </div>
    </div>
  );
}
